// StagePath — gate-aware lifecycle stage rail (done / current / available /
// blocked / muted pills). i18n-clean: every label and blockedReason arrives via
// props; imports only from `./` (ui), never a feature.
import type { CSSProperties } from 'react';
import { Icon } from './Icon.js';

export interface StagePathStage {
  key: string;
  label: string;
  state: 'done' | 'current' | 'available' | 'blocked' | 'muted';
  blockedReason?: string;
  onSelect?: () => void;
}

export interface StagePathProps {
  stages: StagePathStage[];
}

const PILL_BASE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 30,
  padding: '0 14px',
  borderRadius: 'var(--r-full)',
  fontSize: 'var(--fz-13)',
  whiteSpace: 'nowrap',
};

const STATE_STYLE: Record<StagePathStage['state'], CSSProperties> = {
  done: {
    background: 'var(--status-success-bg)',
    color: 'var(--status-success-fg)',
    border: '1px solid transparent',
  },
  current: {
    background: 'var(--status-info-bg)',
    color: 'var(--status-info-fg)',
    border: '1px solid transparent',
    fontWeight: 600,
  },
  available: {
    background: 'var(--bg-1)',
    color: 'var(--fg-2)',
    border: '1px solid var(--border-1)',
  },
  blocked: {
    background: 'var(--bg-1)',
    color: 'var(--fg-4)',
    border: '1px dashed var(--border-strong)',
  },
  muted: {
    background: 'transparent',
    color: 'var(--fg-4)',
    border: '1px solid transparent',
    opacity: 0.6,
  },
};

export function StagePath({ stages }: StagePathProps) {
  if (stages.length === 0) return null;

  return (
    <div role="list" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {stages.map((stage) => {
        const pill: CSSProperties = { ...PILL_BASE, ...STATE_STYLE[stage.state] };
        const leading =
          stage.state === 'done' ? (
            <Icon name="check" size={13} />
          ) : stage.state === 'blocked' ? (
            <Icon name="lock" size={12} />
          ) : null;
        const content = (
          <>
            {leading}
            {stage.label}
          </>
        );

        // Interactive ONLY when the stage is available AND has a handler; every
        // other state (incl. blocked, and available without onSelect) is a
        // non-interactive span. Blocked is never clickable but keeps its title.
        const inner =
          stage.state === 'available' && stage.onSelect !== undefined ? (
            // Reset the default button font; background/border/color come from
            // the pill style so the button matches the static pill exactly.
            <button
              type="button"
              onClick={stage.onSelect}
              aria-label={stage.label}
              style={{ ...pill, cursor: 'pointer', font: 'inherit' }}
            >
              {content}
            </button>
          ) : (
            <span style={pill} title={stage.state === 'blocked' ? stage.blockedReason : undefined}>
              {content}
            </span>
          );

        // The listitem role sits on the wrapper span, not the button, so the
        // button keeps its native role.
        return (
          <span role="listitem" key={stage.key}>
            {inner}
          </span>
        );
      })}
    </div>
  );
}
